const templateName = import.meta.env.VITE_CLERK_JWT_TEMPLATE

export interface ClerkSessionInfo {
  token: string
  userId?: string
  organizationId?: string
  email?: string
}

function isPromise<T>(value: any): value is Promise<T> {
  return value && typeof value.then === 'function'
}

async function loadClerkClient(): Promise<any> {
  if (typeof window === 'undefined') {
    throw new Error('Clerk client not available in this environment')
  }
  const clerk = (window as any).Clerk
  if (!clerk) {
    throw new Error('Clerk client not available')
  }
  if (clerk.loaded) {
    return clerk
  }
  if (typeof clerk.load === 'function') {
    const maybePromise = clerk.load()
    if (isPromise(maybePromise)) {
      await maybePromise
    }
  }
  if (!clerk.loaded && typeof clerk.addListener === 'function') {
    await new Promise<void>((resolve) => {
      const unsubscribe = clerk.addListener(() => {
        if (clerk.loaded) {
          unsubscribe?.()
          resolve()
        }
      })
      // Fallback timeout in case listener never fires
      setTimeout(() => {
        unsubscribe?.()
        resolve()
      }, 3000)
    })
  }
  return clerk
}

export async function getClerkSessionInfo(): Promise<ClerkSessionInfo> {
  const clerk = await loadClerkClient()
  const session = clerk.session
  if (!session) {
    throw new Error('No active Clerk session')
  }

  const getTokenFn = session.getToken?.bind(session) || clerk.session?.getToken?.bind(clerk.session)
  const token = await (getTokenFn ? getTokenFn(templateName ? { template: templateName } : undefined) : null)
  if (!token) {
    throw new Error('Failed to obtain Clerk session token')
  }

  const user = session.user || clerk.user
  const primaryEmail = user?.primaryEmailAddress?.emailAddress
    || user?.emailAddresses?.[0]?.emailAddress
    || clerk?.user?.primaryEmailAddress?.emailAddress

  const organizationId = session.organization?.id
    || session.lastActiveOrganizationId
    || clerk?.organization?.id
    || clerk?.lastActiveOrganizationId

  const userId = user?.id || session.userId || clerk?.user?.id

  return {
    token,
    userId,
    organizationId,
    email: primaryEmail
  }
}
